import { useState } from 'react'
import { kycApi } from '../../../services/api-client'
import type { VendorKYC, TermsData } from '../../../types'

interface Props {
  kyc: VendorKYC | null
  onComplete: (kyc: VendorKYC) => void
  onBack?: () => void
}

export function Step6Terms({ kyc, onComplete, onBack }: Props) {
  const [formData, setFormData] = useState<TermsData>({
    terms_accepted: kyc?.terms_accepted || false,
    privacy_policy_accepted: kyc?.privacy_policy_accepted || false,
    vendor_agreement_accepted: kyc?.vendor_agreement_accepted || false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.terms_accepted || !formData.privacy_policy_accepted || !formData.vendor_agreement_accepted) {
      setError('Debe aceptar todos los términos y condiciones para continuar')
      return
    }

    setError(null)
    setLoading(true)

    try {
      const response = await kycApi.submitStep6(formData)
      onComplete(response.kyc)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al enviar la solicitud')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Términos y Condiciones
        </h2>
        <p className="text-gray-600">
          Por favor, lea y acepte los siguientes términos para completar su registro.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Terms of Service */}
        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="terms"
              checked={formData.terms_accepted}
              onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div className="ml-3">
              <label htmlFor="terms" className="block text-sm font-medium text-gray-900">
                Acepto los Términos y Condiciones <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mt-1">
                He leído y acepto los{' '}
                <a href="/terms" target="_blank" className="text-blue-600 hover:underline">
                  Términos y Condiciones de Uso
                </a>
                {' '}de la plataforma.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="privacy"
              checked={formData.privacy_policy_accepted}
              onChange={(e) => setFormData({ ...formData, privacy_policy_accepted: e.target.checked })}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div className="ml-3">
              <label htmlFor="privacy" className="block text-sm font-medium text-gray-900">
                Acepto la Política de Privacidad <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mt-1">
                He leído y acepto la{' '}
                <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">
                  Política de Privacidad
                </a>
                {' '}y el tratamiento de mis datos personales.
              </p>
            </div>
          </div>
        </div>

        {/* Vendor Agreement */}
        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="vendor"
              checked={formData.vendor_agreement_accepted}
              onChange={(e) => setFormData({ ...formData, vendor_agreement_accepted: e.target.checked })}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div className="ml-3">
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-900">
                Acepto el Acuerdo de Vendedor <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mt-1">
                He leído y acepto el{' '}
                <a href="/vendor-agreement" target="_blank" className="text-blue-600 hover:underline">
                  Acuerdo de Vendedor
                </a>
                {' '}incluyendo las comisiones y condiciones de venta.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          Información Importante
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Su solicitud será revisada por nuestro equipo en un plazo de 2-3 días hábiles</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Recibirá una notificación por correo electrónico sobre el estado de su verificación</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Una vez aprobado, podrá comenzar a publicar productos y recibir pedidos</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>La información proporcionada debe ser precisa y verificable</span>
          </li>
        </ul>
      </div>

      <div className="flex justify-between pt-6 border-t">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Atrás
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !formData.terms_accepted || !formData.privacy_policy_accepted || !formData.vendor_agreement_accepted}
          className={`px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${!onBack ? 'ml-auto' : ''}`}
        >
          {loading ? 'Enviando...' : 'Enviar Solicitud de Verificación'}
        </button>
      </div>
    </form>
  )
}
